"use client";

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CalendarClock,
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  KeyRound,
  Loader2,
  Pause,
  Pencil,
  PhoneIncoming,
  PhoneMissed,
  Play,
  Plus,
  Radio,
  Search,
  Trash2,
  Tv,
  Users,
  Webhook,
  Zap,
} from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Switch } from "@/app/_module/components/ui/switch";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { useFeatureFlag } from "@/app/_module/hooks/useFeatureFlag";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { cn } from "@/app/_module/lib/utils";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";

/**
 * Live Transfer System — owner-only ring-group campaigns.
 *
 * Moe's flow, verbatim: pick a number → pick Round Robin or Round Blast →
 * pick the agents on that number → finish. Feature-flag `liveTransfer`,
 * owner-only (both enforced again server-side).
 */

type Mode = "round_robin" | "round_blast";


interface Campaign {
  id: string;
  name: string;
  phoneNumber: string;
  phoneNumberId: string;
  mode: Mode;
  isActive: boolean;
  appointmentAssignmentMode?: "owner_triage" | "auto_assign";
  /** Tag + Lead Source on this campaign's leads. */
  leadLabel?: string;
  /** Said before the ring, asking the caller's name (TV lines). Null rings straight away. */
  greeting?: string | null;
  agents: Array<{ id: string; name: string; email: string } & Presence>;
}
interface AvailableNumber {
  id: string;
  phoneNumber: string;
  /** Effective owner (assigned_to, else purchaser) — the agent shown as "Owner:" below. */
  ownerUserId: string | null;
  ownerName: string | null;
  inUse: boolean;
  campaignName: string | null;
}
/** Who picked up is the agent's claim, never the call record's owner (that's the number's owner). */
interface CallOutcome {
  outcome?: "answered" | "missed" | "in_progress";
  answeredById?: string | null;
  answeredByName: string | null;
  /** Clicked answer after the caller had already hung up. */
  lateClaimByName?: string | null;
  durationSeconds: number | null;
  /** How long a missed caller stayed on the line. */
  waitedSeconds?: number | null;
  voicemailLeft?: boolean;
  rangAgents?: Array<{ id: string; name: string }>;
}
interface ActivityEvent extends CallOutcome {
  id: string;
  callerNumber: string;
  mode: Mode;
  rangAgentIds: string[];
  createdAt: string;
  leadId: string | null;
  leadName: string | null;
  leadStage: string | null;
  leadAssignedToName: string | null;
  callStatus: string | null;
  /** From the mortgage AI's own line and nobody we could name: its number isn't the caller's. */
  unknownAiTransfer?: boolean;
}
interface AgentStat {
  userId: string;
  name: string;
  rang: number;
  answered: number;
}
interface ApiAppointment {
  id: string;
  startTime: string;
  endTime: string | null;
  status: string | null;
  bookedAt: string;
  leadId: string | null;
  leadName: string;
  leadPhone: string | null;
  leadState: string | null;
  agentName: string | null;
  campaignName: string | null;
  campaignPhone: string | null;
}
interface ApiAppointmentsData {
  kpis: { total: number; upcoming: number; last7d: number; cancelled: number };
  rows: ApiAppointment[];
}
interface CampaignActivity {
  events: ActivityEvent[];
  agentStats: AgentStat[];
  totals: { totalCalls: number; answeredInWindow: number; windowSize: number };
}

interface Overview {
  kpis: { callsToday: number; answeredToday: number; callsAllTime: number; answeredAllTime: number };
  series: Array<{ day: string; calls: number; answered: number }>;
  recent: Array<CallOutcome & {
    id: string; callerNumber: string; mode: Mode; createdAt: string; rangCount: number;
    campaignName: string; leadName: string | null; leadId: string | null;
    /** Who has the lead now. A missed transfer lands with the campaign owner. */
    leadAssignedToName?: string | null;
    unknownAiTransfer?: boolean;
  }>;
}

/** Mirrors the ring rule: user_status in available/idle/on_call AND seen in the last 20 min. */
interface Presence {
  online?: boolean;
  userStatus?: string | null;
  lastSeenAt?: string | null;
}
interface EligibleAgent extends Presence {
  id: string;
  name: string;
  email: string;
  agencyId: string;
  agencyName: string;
  /** 0 = the owner's own agency; deeper = further down the tree. */
  depth: number;
}

const fetchJson = async <T,>(url: string): Promise<T> =>
  handleApiCall(async () => {
    // The global response interceptor wraps controller returns, and the
    // controller returns {statusCode, data} itself — unwrap until payload.
    let v: any = (await api.get({ url: getApiUrl(url), auth: true })) as any;
    for (let i = 0; i < 4 && v && typeof v === "object" && "data" in v && !Array.isArray(v); i++) {
      v = v.data;
    }
    return v as T;
  });

// "Today" and the 7-day chart are this browser's calendar days, not the server's UTC ones.
const overviewUrl = () =>
  `${endpoint.liveTransfer.overview}?tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || "")}`;

/** Matches the backend default for a campaign's tag + Lead Source. */
const DEFAULT_LEAD_LABEL = "Mortgage Live Transfers";

/** The TV commercial line's greeting, the starting text when it's turned on (`tvTransfers` flag). */
const TV_GREETING =
  "Thanks for calling Community Policy Reviewers, your community driven source for all of your insurance needs. Please state your first and last name while we connect you.";
/** The tag a TV line's leads get when the greeting is turned on over the default tag. */
const TV_LEAD_LABEL = "TV Ad";

const fetchJsonPost = async <T,>(url: string, body?: object): Promise<T> =>
  handleApiCall(async () => {
    let v: any = (await api.post({ url: getApiUrl(url), auth: true, body: body ?? {} })) as any;
    for (let i = 0; i < 4 && v && typeof v === "object" && "data" in v && !Array.isArray(v); i++) {
      v = v.data;
    }
    return v as T;
  });

const fmtPhone = (e164: string) => {
  const d = (e164 || "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : e164;
};

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

export default function LiveTransferPage() {
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Campaign | null>(null);
  const [editTarget, setEditTarget] = useState<Campaign | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ["live-transfer", "campaigns"],
    queryFn: () => fetchJson<Campaign[]>(endpoint.liveTransfer.campaigns),
    staleTime: 30_000,
    refetchInterval: 30_000, // presence dots, heartbeat is 2 min, cleanup ~15
  });

  const toggleActive = useMutation({
    mutationFn: (c: Campaign) =>
      handleApiCall(async () =>
        api.patch({
          url: getApiUrl(endpoint.liveTransfer.campaign(c.id)),
          auth: true,
          body: { isActive: !c.isActive },
        }),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["live-transfer"] }),
  });

  const deleteCampaign = useMutation({
    mutationFn: (id: string) =>
      handleApiCall(async () =>
        api.delete({ url: getApiUrl(endpoint.liveTransfer.campaign(id)), auth: true }),
      ),
    onSettled: () => {
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ["live-transfer"] });
    },
  });

  return (
    <div id="lt-root" className="mx-auto max-w-5xl space-y-6 p-6">
      <style>{RING_CSS}</style>

      <CommandDeck campaigns={campaigns} onNewCampaign={() => setWizardOpen(true)} />

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !campaigns?.length ? (
        <div className="rounded-2xl border border-dashed p-14 text-center">
          <div className="flex justify-center">
            <RingVisual mode="round_blast" />
          </div>
          <h2 className="mt-5 text-xl font-semibold tracking-tight">Put a number on a ring group</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Every call to it rings the agents you pick, all at once or one per call in turn.
            First to answer takes the lead.
          </p>
          <Button onClick={() => setWizardOpen(true)} className="mt-6 gap-1.5">
            <Plus className="h-4 w-4" />
            Create your first campaign
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((c) => {
            const online = c.agents.filter((a) => a.online).length;
            const state = !c.isActive
              ? { word: "Paused", cls: "text-muted-foreground", dot: "bg-muted-foreground/40" }
              : online > 0
                ? { word: "Ready to ring", cls: "text-emerald-600", dot: "bg-emerald-500" }
                : { word: "Nobody online to answer", cls: "text-amber-600", dot: "bg-amber-500" };
            return (
              <article
                key={c.id}
                className={cn("rounded-2xl border bg-card", !c.isActive && "opacity-70")}
              >
                <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 p-5 pb-4">
                  <div className="min-w-0">
                    <h2 className="text-[26px] font-semibold leading-none tabular-nums tracking-tight">
                      {fmtPhone(c.phoneNumber)}
                    </h2>
                    <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                      {c.mode === "round_blast" ? (
                        <BlastGlyph className="h-4 w-4 shrink-0 text-foreground/70" />
                      ) : (
                        <RobinGlyph className="h-4 w-4 shrink-0 text-foreground/70" />
                      )}
                      <span className="truncate">
                        <span className="font-medium text-foreground">{c.name}</span>
                        {" · "}
                        {c.mode === "round_blast"
                          ? "everyone rings at once, first pickup wins"
                          : "rings one agent at a time, in turn"}
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Leads tagged <span className="font-medium text-foreground">{c.leadLabel || DEFAULT_LEAD_LABEL}</span>
                    </p>
                    {c.greeting && (
                      <p className="mt-1 line-clamp-1 max-w-xl text-xs text-muted-foreground" title={c.greeting}>
                        Greets callers first: <span className="text-foreground">&ldquo;{c.greeting}&rdquo;</span>
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className={cn("flex items-center justify-end gap-2 font-medium", state.cls)}>
                      <span className={cn("inline-block h-2 w-2 rounded-full", state.dot, c.isActive && online > 0 && "lt-beat")} />
                      {state.word}
                    </p>
                    <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                      {online} of {c.agents.length} {c.agents.length === 1 ? "agent" : "agents"} online right now
                    </p>
                  </div>
                </div>

                {c.agents.length > 0 && (
                  <div className="mx-5 mb-4 rounded-xl bg-muted/40 px-5 pb-4 pt-7">
                    <RingLane campaign={c} />
                  </div>
                )}

                {/* Roster: who this line rings, and who would actually pick up right now */}
                <ul className="grid gap-x-8 gap-y-1.5 px-5 pb-4 sm:grid-cols-2 lg:grid-cols-3">
                  {c.agents.map((a) => {
                    const p = presenceOf(a);
                    return (
                      <li key={a.id} title={`${a.name} · ${p.label}`} className="flex min-w-0 items-center gap-2 text-sm">
                        <span className={cn("h-2 w-2 shrink-0 rounded-full", p.dot)} />
                        <span className={cn("truncate", a.online ? "font-medium" : "text-muted-foreground")}>{a.name}</span>
                        <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">{p.short}</span>
                      </li>
                    );
                  })}
                  {c.agents.length === 0 && (
                    <li className="text-sm text-muted-foreground">No agents on this line. Edit it to add some.</li>
                  )}
                </ul>

                <div className="flex flex-wrap items-center gap-1.5 border-t px-3 py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1.5"
                    disabled={toggleActive.isPending}
                    onClick={() => toggleActive.mutate(c)}
                  >
                    {c.isActive ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    {c.isActive ? "Pause" : "Resume"}
                  </Button>
                  <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setEditTarget(c)}>
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 text-muted-foreground hover:text-red-600"
                    onClick={() => setDeleteTarget(c)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto gap-1"
                    aria-expanded={expandedId === c.id}
                    onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                  >
                    Call log
                    <ChevronDown
                      className={cn("h-3.5 w-3.5 transition-transform", expandedId === c.id && "rotate-180")}
                    />
                  </Button>
                </div>
                {expandedId === c.id && <CampaignActivityPanel campaign={c} />}
              </article>
            );
          })}
        </div>
      )}

      <OverviewDashboard hasCampaigns={!!campaigns?.length} />

      <ApiAppointmentsPanel />

      <LeadSourcePanel firstNumber={campaigns?.find((c) => c.isActive)?.phoneNumber} />
      <AppointmentApiPanel firstNumber={campaigns?.find((c) => c.isActive)?.phoneNumber} />

      <CampaignWizard open={wizardOpen} onOpenChange={setWizardOpen} />
      <CampaignWizard
        key={editTarget?.id ?? "none"}
        open={!!editTarget}
        campaign={editTarget}
        onOpenChange={(v) => {
          if (!v) setEditTarget(null);
        }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Calls to {deleteTarget ? fmtPhone(deleteTarget.phoneNumber) : "this number"} will go
              back to ringing the number&apos;s owner directly. This frees the number for a new
              campaign.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteTarget && deleteCampaign.mutate(deleteTarget.id)}
            >
              {deleteCampaign.isPending ? "Deleting…" : "Delete campaign"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ─── Ring animations: live round-robin / blast visuals ─────────────────── */

/**
 * One global stylesheet for every ring animation on the page (mode cards,
 * empty state, campaign-row icons). Rendered once at page root — CSS is
 * global, so the wizard dialog portal picks it up too. All motion is
 * disabled under prefers-reduced-motion.
 */
const RING_CSS = `
.lt-anim .lt-caller::after{content:"";position:absolute;inset:0;border-radius:9999px;border:1.5px solid var(--primary);opacity:0;animation:ltPulse 2.2s ease-out infinite}
@keyframes ltPulse{0%{transform:scale(1);opacity:.7}70%{transform:scale(1.7);opacity:0}100%{transform:scale(1.7);opacity:0}}
.lt-anim .lt-sig{position:absolute;top:-2px;left:0;height:5px;width:5px;border-radius:9999px;background:var(--primary);opacity:0;animation:ltTravel 1.35s linear infinite}
@keyframes ltTravel{0%{left:0;opacity:0}15%{opacity:.9}85%{opacity:.9}100%{left:calc(100% - 5px);opacity:0}}
.lt-anim .lt-rr{animation:ltRR 4.4s ease-in-out infinite}
@keyframes ltRR{0%,26%,100%{border-color:var(--border);color:var(--muted-foreground);box-shadow:none;transform:scale(1)}6%,20%{border-color:var(--primary);color:var(--primary);box-shadow:0 0 0 4px color-mix(in oklab,var(--primary) 20%,transparent);transform:scale(1.12)}}
.lt-anim .lt-blast{animation:ltBlast 3.6s ease-in-out infinite}
@keyframes ltBlast{0%,52%,100%{border-color:var(--border);color:var(--muted-foreground);box-shadow:none;transform:scale(1)}8%,40%{border-color:#f59e0b;color:#f59e0b;box-shadow:0 0 0 4px rgba(245,158,11,.18);transform:scale(1.08)}}
.lt-anim .lt-win{animation:ltBlastWin 3.6s ease-in-out infinite}
@keyframes ltBlastWin{0%,100%{border-color:var(--border);color:var(--muted-foreground);box-shadow:none;transform:scale(1)}8%,40%{border-color:#f59e0b;color:#f59e0b;box-shadow:0 0 0 4px rgba(245,158,11,.18);transform:scale(1.08)}54%,86%{border-color:#10b981;color:#10b981;box-shadow:0 0 0 5px rgba(16,185,129,.22);transform:scale(1.15)}}
.lt-iconrr{position:relative}
.lt-iconrr::after{content:"";position:absolute;inset:-3px;border-radius:12px;border:1.5px dashed var(--primary);opacity:.5;animation:ltSpin 7s linear infinite}
@keyframes ltSpin{to{transform:rotate(360deg)}}
.lt-iconblast{position:relative}
.lt-iconblast::after{content:"";position:absolute;inset:-3px;border-radius:12px;border:1.5px solid #f59e0b;opacity:0;animation:ltPulse 2.4s ease-out infinite}
.lt-anim .lt-caller::before{content:"";position:absolute;inset:0;border-radius:9999px;border:1.5px solid var(--primary);opacity:0;animation:ltPulse 2.2s ease-out 1.1s infinite}
.lt-anim .lt-sig{top:-3px;height:7px;width:7px;box-shadow:0 0 10px 2px color-mix(in oklab,var(--primary) 55%,transparent)}
.lt-anim .lt-avatar::after{content:"";position:absolute;inset:-3px;border-radius:9999px;border:2px solid currentColor;opacity:0;pointer-events:none}
.lt-anim .lt-blast.lt-avatar::after{animation:ltWave 3.6s ease-out infinite}
.lt-anim .lt-win.lt-avatar::after{animation:ltWaveWin 3.6s ease-out infinite}
.lt-anim .lt-rr.lt-avatar::after{animation:ltWaveRR var(--lt-dur,4.4s) ease-out var(--lt-delay,0s) infinite}
@keyframes ltWave{0%,8%{transform:scale(1);opacity:0}14%{opacity:.8}46%{transform:scale(1.9);opacity:0}100%{transform:scale(1.9);opacity:0}}
@keyframes ltWaveWin{0%,8%{transform:scale(1);opacity:0}14%{opacity:.8}46%{transform:scale(1.9);opacity:0}54%{transform:scale(1);opacity:.9}88%{transform:scale(2.2);opacity:0}100%{opacity:0}}
@keyframes ltWaveRR{0%,6%{transform:scale(1);opacity:0}10%{opacity:.8}28%{transform:scale(1.8);opacity:0}100%{transform:scale(1.8);opacity:0}}
.lt-ecg path{stroke-dasharray:28 72;animation:ltEcg 2.4s linear infinite}
@keyframes ltEcg{from{stroke-dashoffset:100}to{stroke-dashoffset:0}}
.lt-beat{animation:ltBeat 2s ease-in-out infinite}
@keyframes ltBeat{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.4);opacity:.55}}
.lt-new{animation:ltNew 2.4s ease-out 1}
@keyframes ltNew{from{background:color-mix(in oklab,var(--primary) 14%,transparent)}to{background:transparent}}
@media (prefers-reduced-motion:reduce){.lt-anim *,.lt-anim .lt-caller::before,.lt-anim .lt-avatar::after,.lt-iconrr::after,.lt-iconblast::after,.lt-beat,.lt-new,.lt-ecg path{animation:none!important}.lt-anim .lt-sig{display:none}}
`;

/**
 * Caller → signal → four agents, animated per mode:
 * round_robin — agents light up one at a time, in order (the rotation).
 * round_blast — everyone rings together, then one goes green (first answer).
 */
function RingVisual({ mode, compact = false }: { mode: Mode; compact?: boolean }) {
  const rr = mode === "round_robin";
  const dot = compact ? "h-6 w-6" : "h-9 w-9";
  const icon = compact ? "h-3 w-3" : "h-4 w-4";
  return (
    <div className={cn("lt-anim flex items-center", compact ? "gap-1.5" : "gap-2.5")} aria-hidden>
      <div
        className={cn(
          "lt-caller relative flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground",
          dot,
        )}
      >
        <PhoneIncoming className={icon} />
      </div>
      <div className={cn("relative h-px shrink-0 bg-border", compact ? "w-6" : "w-9")}>
        {[0, 1, 2].map((i) => (
          <span key={i} className="lt-sig" style={{ animationDelay: `${i * 0.45}s` }} />
        ))}
      </div>
      <div className={cn("flex", compact ? "gap-1.5" : "gap-2")}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={cn(
              "flex shrink-0 items-center justify-center rounded-full border bg-card text-muted-foreground",
              dot,
              rr ? "lt-rr" : i === 1 ? "lt-blast lt-win" : "lt-blast",
            )}
            style={rr ? { animationDelay: `${i * 1.1}s` } : undefined}
          >
            <Users className={icon} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Lead source API: connect an external funnel (mortgage etc.) ────────── */

interface ApiAccessStatus {
  hasAccess: boolean;
  status: "pending" | "active" | "denied" | "suspended" | "revoked" | null;
  apiKeyPrefix?: string;
  rateLimitPerMinute?: number;
  denialReason?: string;
  stats?: {
    totalLeadsSubmitted: number;
    totalLeadsSuccess: number;
    totalLeadsFailed: number;
  };
}

interface AppointmentApiAccess {
  id: string;
  name: string;
  status: string;
  apiKeyPrefix?: string;
  rosterMode?: string;
  rateLimitPerMinute?: number;
  bookingLimitPerMinute?: number;
  campaignIds?: string[];
  totalAppointmentsBooked?: number;
  lastApiCallAt?: string | null;
}

/**
 * "Connect your lead source" — surfaces the existing Agent API (the same one
 * AgentVerified syncs through) with Live-Transfer-specific instructions, so a
 * vendor/funnel can push the lead BEFORE transferring the call. The endpoint
 * and example are ALWAYS visible (integrators read before they connect); only
 * the key differs by state. Live Transfer users are pre-vetted by the feature
 * flag, so key creation is one click — no application, no review. Key
 * management (show-once, regenerate, revoke) stays in Settings → API Access.
 */
function LeadSourcePanel({ firstNumber }: { firstNumber?: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  // The full key exists client-side only in this state, only until refresh —
  // same show-once contract as admin approval.
  const [freshKey, setFreshKey] = useState<string | null>(null);

  const { data: access } = useQuery({
    queryKey: ["live-transfer", "api-access"],
    queryFn: () => fetchJson<ApiAccessStatus>(endpoint.agentApi.status),
    staleTime: 60_000,
  });

  // Live Transfer users skip the application queue — the feature flag is the
  // vetting, so the backend creates AND approves the key in one call.
  const generateKey = useMutation({
    mutationFn: () =>
      fetchJsonPost<{ apiKey?: string; apiKeyPrefix?: string; alreadyActive?: boolean }>(
        endpoint.liveTransfer.apiAccess,
      ),
    onSuccess: (d) => {
      if (d?.apiKey) setFreshKey(d.apiKey);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["live-transfer", "api-access"] }),
  });

  // Vendors POST to the FRONTEND origin — next.config rewrites proxy
  // /api/v1/agent-api/* to the backend (same presentation as Settings →
  // API Access; never leak the raw backend host).
  const postUrl = `${
    typeof window !== "undefined" ? window.location.origin : "https://app.insuracentral.com"
  }${endpoint.agentApi.submitLeads}`;
  const keySample = access?.apiKeyPrefix ? `${access.apiKeyPrefix}…` : "aa_YOUR_KEY";
  const curl = [
    `curl -X POST '${postUrl}' \\`,
    `  -H 'X-API-Key: ${keySample}' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -d '{"leads":[{`,
    `    "first_name": "Jane",`,
    `    "last_name": "Doe",`,
    `    "phone": "+13045551234",`,
    `    "email": "jane@example.com",`,
    `    "state": "FL",`,
    `    "notes": "Refi inquiry, $320k, credit 700+",`,
    `    "custom_fields": {"loan_amount": "320000"}`,
    `  }]}'`,
  ].join("\n");

  const copy = (label: string, text: string) => {
    navigator.clipboard.writeText(text).catch(() => undefined);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="rounded-xl border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Webhook className="h-4.5 w-4.5" />
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              Connect your lead source
              {access?.status === "active" && (
                <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600">
                  Connected
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Sync leads from your funnel via API so transfer calls ring with full lead details.
            </p>
          </div>
        </div>
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="space-y-4 border-t p-4">
          {/* How it works — same story in every state */}
          <ol className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
            <li className="rounded-lg bg-muted/50 p-3">
              <span className="font-semibold text-foreground">1 · Push the lead</span>
              <br />
              POST it from your funnel the moment the caller opts in.
            </li>
            <li className="rounded-lg bg-muted/50 p-3">
              <span className="font-semibold text-foreground">2 · Transfer the call</span>
              <br />
              Forward the caller to your campaign number
              {firstNumber ? ` (${fmtPhone(firstNumber)})` : ""}.
            </li>
            <li className="rounded-lg bg-muted/50 p-3">
              <span className="font-semibold text-foreground">3 · First answer takes it</span>
              <br />
              Agents ring with the lead&apos;s name; the answerer gets it assigned.
            </li>
          </ol>
          <p className="text-[11px] text-muted-foreground">
            Skip step 1 and calls still work, we auto-create the lead from caller ID. Pushing
            first just means agents see who&apos;s calling. Matching is by phone number, any format.
          </p>

          {/* Endpoint + example — always visible so integrators can read ahead */}
          <div>
            <Label className="text-xs">Endpoint</Label>
            <div className="mt-1 flex items-center gap-2">
              <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md bg-muted px-2.5 py-1.5 font-mono text-xs">
                POST {postUrl}
              </code>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => copy("url", postUrl)}
              >
                {copied === "url" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                Copy
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs">Example request</Label>
            <div className="relative mt-1">
              <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-[11px] leading-relaxed">
                {curl}
              </pre>
              <Button
                variant="outline"
                size="sm"
                className="absolute right-2 top-2 gap-1.5"
                onClick={() => copy("curl", curl)}
              >
                {copied === "curl" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                Copy
              </Button>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground">
            Only <code className="font-mono">phone</code> is required, everything else (name,
            email, address, DOB, notes, custom fields) enriches the lead. Up to 100 leads per
            request. Send the header <code className="font-mono">X-API-Key</code> with every call.
          </p>

          {/* State-specific: the key itself */}
          {access?.status === "active" ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <KeyRound className="h-3.5 w-3.5" />
                Key: <code className="font-mono">{access.apiKeyPrefix}…</code>
              </span>
              {typeof access.rateLimitPerMinute === "number" && (
                <span>{access.rateLimitPerMinute} req/min</span>
              )}
              {access.stats && (
                <span>
                  {access.stats.totalLeadsSubmitted.toLocaleString()} leads synced
                  {access.stats.totalLeadsFailed > 0 &&
                    ` · ${access.stats.totalLeadsFailed.toLocaleString()} failed`}
                </span>
              )}
              <a
                href="/settings?tab=account&accountTab=api-access"
                className="flex items-center gap-1 text-primary hover:underline"
              >
                Manage key <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ) : access?.status === "denied" ? (
            <p className="rounded-lg border-l-2 border-l-red-400 bg-red-50/60 p-3 text-xs text-red-800">
              Your previous API access request was declined
              {access.denialReason ? `: ${access.denialReason}` : "."} Contact support to appeal.
            </p>
          ) : (
            <div className="space-y-3">
              {freshKey ? (
                <div className="space-y-2">
                  <Label className="text-xs">Your API key, copy it now, it will not be shown again</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-amber-300 bg-amber-50/60 px-2.5 py-1.5 font-mono text-xs">
                      {freshKey}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => copy("key", freshKey)}
                    >
                      {copied === "key" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      Copy
                    </Button>
                  </div>
                  <p className="text-[11px] text-amber-700">
                    Store it somewhere safe, after you leave this page only the prefix is
                    visible. Lost keys can be regenerated in Settings → API Access.
                  </p>
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="max-w-md text-xs text-muted-foreground">
                    Live Transfer includes lead-source API access, no application, no review.
                    One click creates your key.
                  </p>
                  <Button
                    size="sm"
                    disabled={generateKey.isPending}
                    onClick={() => generateKey.mutate()}
                    className="gap-1.5"
                  >
                    {generateKey.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <KeyRound className="h-3.5 w-3.5" />
                    )}
                    Generate my API key
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Live ring board: real campaigns, real agents, animated routing ────── */

/* ─── Command deck: dark hero with KPIs + live ring board ───────────────── */

/**
 * The page's opening shot: a self-contained dark glass panel (works on both
 * themes) holding the title, today's numbers as glass chips, and one animated
 * ring lane per ACTIVE campaign — cast with the campaign's real agents. Reads
 * as a dispatch board, not a report.
 */

/**
 * "Appointment setting" — the AI's booking key, sibling of LeadSourcePanel.
 *
 * A SEPARATE key from the lead-source one, deliberately. That key is bound to
 * one user and pushes leads TO them; this one is bound to the agency and books
 * onto whichever campaign OWNER is next in the rotation. Keeping them apart
 * means booking access can be revoked without stopping lead flow.
 *
 * Provisioned in one click for the same reason as the lead-source key: the
 * feature flag plus agency-owner role is already a stricter gate than any
 * review would be.
 */
function AppointmentApiPanel({ firstNumber }: { firstNumber?: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [freshKey, setFreshKey] = useState<string | null>(null);

  const { data: access } = useQuery({
    queryKey: ["live-transfer", "appointment-api"],
    queryFn: async () => {
      const r = await fetchJson<{ access: AppointmentApiAccess | null }>(
        endpoint.liveTransfer.appointmentApiStatus,
      );
      return r?.access ?? null;
    },
    staleTime: 60_000,
  });

  const generateKey = useMutation({
    mutationFn: () =>
      fetchJsonPost<{ apiKey?: string; apiKeyPrefix?: string; alreadyActive?: boolean }>(
        endpoint.liveTransfer.appointmentApi,
      ),
    onSuccess: (d) => {
      if (d?.apiKey) setFreshKey(d.apiKey);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["live-transfer", "appointment-api"] }),
  });

  // Rotation is the ONLY recovery path for a lost key — it is shown once and
  // cannot be read back. Without this an owner who loses it is locked out of
  // their own integration.
  const regenerate = useMutation({
    mutationFn: () =>
      fetchJsonPost<{ apiKey?: string; apiKeyPrefix?: string }>(
        endpoint.liveTransfer.appointmentApiRegenerate,
      ),
    onSuccess: (d) => {
      if (d?.apiKey) setFreshKey(d.apiKey);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["live-transfer", "appointment-api"] }),
  });

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://app.insuracentral.com";
  const bookUrl = `${origin}/api/v1/appointment-api/appointments`;
  const keySample = access?.apiKeyPrefix ? `${access.apiKeyPrefix}…` : "ap_YOUR_KEY";
  const curl = [
    `curl -X POST '${bookUrl}' \\`,
    `  -H 'X-API-Key: ${keySample}' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -d '{`,
    `    "campaignPhoneNumber": "${firstNumber || "+13045551234"}",`,
    `    "startTime": "2026-09-03T14:00:00-05:00",`,
    `    "durationMins": 30,`,
    `    "notes": "Declined live transfer, asked for Thursday afternoon.",`,
    `    "lead": {"phone": "+13045551234", "firstName": "Jane", "lastName": "Doe", "state": "FL"}`,
    `  }'`,
  ].join("\n");

  const copy = (label: string, text: string) => {
    navigator.clipboard.writeText(text).catch(() => undefined);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="rounded-xl border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <CalendarClock className="h-4.5 w-4.5" />
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              Appointment setting
              {access?.status === "active" && (
                <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600">
                  Connected
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Let your AI book a callback when the caller doesn&apos;t want transferring right now.
            </p>
          </div>
        </div>
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="space-y-4 border-t p-4">
          <ol className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
            <li className="rounded-lg bg-muted/50 p-3">
              <span className="font-semibold text-foreground">1 · Caller declines transfer</span>
              <br />
              Your AI offers a time instead of forwarding the call.
            </li>
            <li className="rounded-lg bg-muted/50 p-3">
              <span className="font-semibold text-foreground">2 · Check who&apos;s free</span>
              <br />
              GET /availability returns each owner&apos;s booked blocks.
            </li>
            <li className="rounded-lg bg-muted/50 p-3">
              <span className="font-semibold text-foreground">3 · Book it</span>
              <br />
              Lands on the next campaign owner&apos;s calendar, like any appointment.
            </li>
          </ol>
          <p className="text-[11px] text-muted-foreground">
            Appointments rotate between the <strong>agency owners on that campaign</strong>, the
            same roster the ring group uses, minus anyone who isn&apos;t an owner. Add or remove an
            agent on the campaign and the very next booking follows; nothing to sync. Anyone
            already booked at that time is skipped, and if they all are you get a 409 so the AI
            can offer another slot.
          </p>

          <div>
            <Label className="text-xs">Endpoint</Label>
            <div className="mt-1 flex items-center gap-2">
              <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md bg-muted px-2.5 py-1.5 font-mono text-xs">
                POST {bookUrl}
              </code>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => copy("aurl", bookUrl)}>
                {copied === "aurl" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                Copy
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs">Example request</Label>
            <div className="relative mt-1">
              <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-[11px] leading-relaxed">
                {curl}
              </pre>
              <Button
                variant="outline"
                size="sm"
                className="absolute right-2 top-2 gap-1.5"
                onClick={() => copy("acurl", curl)}
              >
                {copied === "acurl" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                Copy
              </Button>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground">
            <code className="font-mono">startTime</code> must carry a UTC offset, a bare
            local time is ambiguous and would book the wrong hour. Also available:{" "}
            <code className="font-mono">GET /availability</code>,{" "}
            <code className="font-mono">GET /campaigns</code> and{" "}
            <code className="font-mono">GET /docs</code>.
          </p>

          {/* ⚠️ freshKey is checked FIRST, and deliberately so. Creating a key
              triggers a refetch that flips status to "active"; if that branch
              were tested first it would replace the one and only display of the
              key with the prefix view, and the key would be unrecoverable. It
              stays on screen until explicitly dismissed. */}
          {freshKey ? (
            <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50/60 p-3">
              <Label className="text-xs font-semibold">
                Your API key, copy it now. This is the only time it is shown.
              </Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-amber-300 bg-white px-2.5 py-1.5 font-mono text-xs">
                  {freshKey}
                </code>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => copy("akey", freshKey)}>
                  {copied === "akey" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  Copy
                </Button>
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] text-amber-700">
                  Store it somewhere safe. Once dismissed, only the prefix is visible, losing it
                  means issuing a new key.
                </p>
                <Button size="sm" variant="ghost" className="shrink-0" onClick={() => setFreshKey(null)}>
                  I&apos;ve saved it
                </Button>
              </div>
            </div>
          ) : access?.status === "active" ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <KeyRound className="h-3.5 w-3.5" />
                Key: <code className="font-mono">{access.apiKeyPrefix}…</code>
              </span>
              <span>{access.bookingLimitPerMinute ?? 300} bookings/min</span>
              <span>{(access.totalAppointmentsBooked ?? 0).toLocaleString()} appointments booked</span>
              <button
                type="button"
                onClick={() => {
                  if (
                    window.confirm(
                      "Issue a new key? The current one stops working immediately, anything already using it must be updated.",
                    )
                  ) {
                    regenerate.mutate();
                  }
                }}
                disabled={regenerate.isPending}
                className="text-primary hover:underline disabled:opacity-50"
              >
                {regenerate.isPending ? "Issuing…" : "Lost the key? Issue a new one"}
              </button>
            </div>
          ) : (
            <Button size="sm" onClick={() => generateKey.mutate()} disabled={generateKey.isPending}>
              {generateKey.isPending ? "Generating…" : "Generate appointment API key"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}


function CommandDeck({
  campaigns,
  onNewCampaign,
}: {
  campaigns?: Campaign[];
  onNewCampaign: () => void;
}) {
  const { data } = useQuery({
    queryKey: ["live-transfer", "overview"],
    queryFn: () => fetchJson<Overview>(overviewUrl()),
    refetchInterval: 15_000,
  });

  const kpis = data?.kpis;
  const live = (campaigns || []).filter((c) => c.isActive);
  const onlineAgents = [...new Map(live.flatMap((c) => c.agents.filter((a) => a.online).map((a) => [a.id, a]))).values()];
  const onFloor = onlineAgents.length;
  const covered = live.length > 0 && onFloor > 0;
  // "Bruce", "Bruce and Alfred", "Bruce, Alfred and 3 more"
  const firstNames = onlineAgents.map((a) => a.name.split(" ")[0]);
  const who =
    firstNames.length <= 2
      ? firstNames.join(" and ")
      : `${firstNames.slice(0, 2).join(", ")} and ${firstNames.length - 2} more`;
  const lineWord = live.length === 1 ? fmtPhone(live[0].phoneNumber) : `${live.length} lines`;
  const missedToday = kpis ? kpis.callsToday - kpis.answeredToday : 0;
  const rate =
    kpis && kpis.callsToday > 0 ? Math.round((kpis.answeredToday / kpis.callsToday) * 100) : null;

  return (
    <header className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Live Transfer</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Numbers that ring a group of your agents. First to pick up takes the lead.
          </p>
        </div>
        <Button onClick={onNewCampaign} className="gap-1.5">
          <Plus className="h-4 w-4" />
          New campaign
        </Button>
      </div>

      {/* Today's tape: real counts only, hairlines instead of cards */}
      {kpis && (
        <dl className="grid grid-cols-2 overflow-hidden rounded-xl border bg-card sm:grid-cols-4 sm:divide-x">
          {[
            { k: "Calls today", v: kpis.callsToday, sub: `${kpis.callsAllTime} all time` },
            { k: "Answered", v: kpis.answeredToday, sub: `${kpis.answeredAllTime} all time`, cls: kpis.answeredToday > 0 ? "text-emerald-600" : "" },
            { k: "Missed", v: missedToday, sub: "today", cls: missedToday > 0 ? "text-red-600" : "" },
            { k: "Answer rate", v: rate === null ? "–" : `${rate}%`, sub: rate === null ? "no calls yet today" : "today" },
          ].map((c) => (
            <div key={c.k} className="px-4 py-3">
              <dt className="text-xs text-muted-foreground">{c.k}</dt>
              <dd className={cn("mt-0.5 text-3xl font-semibold tabular-nums tracking-tight", c.cls)}>{c.v}</dd>
              <dd className="text-xs text-muted-foreground">{c.sub}</dd>
            </div>
          ))}
        </dl>
      )}

      {/* Live line: the one moving thing on the page, and only while calls can actually be caught */}
      {campaigns && campaigns.length > 0 && (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          {covered ? (
            <svg className="lt-ecg h-6 w-24 text-emerald-500" viewBox="0 0 96 24" fill="none" aria-hidden>
              <path
                d="M0 12h16l4-8 6 16 5-12 4 4h12l4-6 6 12 5-10 4 4h30"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
                strokeLinecap="round"
                pathLength="100"
              />
            </svg>
          ) : (
            <span className={cn("inline-block h-2 w-2 rounded-full", live.length > 0 ? "bg-amber-500" : "bg-muted-foreground/40")} />
          )}
          <span className="font-medium">
            {live.length === 0
              ? "All lines paused."
              : covered
                ? `${who} ${onFloor === 1 ? "is" : "are"} online.`
                : `${lineWord} ${live.length === 1 ? "is" : "are"} live, nobody is online.`}
          </span>
          <span className="text-muted-foreground">
            {covered
              ? `Calls to ${lineWord} ring ${onFloor === 1 ? "them" : "them all"} the second they come in.`
              : live.length > 0
                ? "A call now would ring out unanswered. Get an agent into the CRM."
                : "Resume a line to start taking calls."}
          </span>
          <span className="ml-auto text-xs text-muted-foreground">Board refreshes every 15 s</span>
        </p>
      )}
    </header>
  );
}

/* ─── Trends: 7-day pulse + cross-campaign feed (KPIs live on the deck) ─── */

/**
 * The ring choreography cast with the campaign's real agents: round robin
 * lights them one at a time in order, blast rings everyone and one goes green.
 * Reads as "this is how my calls route right now", so it sits on every card.
 */
function RingLane({ campaign }: { campaign: Campaign }) {
  const rr = campaign.mode === "round_robin";
  const shown = campaign.agents; // the whole ring group, no "+N" hiding
  // Round robin walks the FULL list, so the cycle scales with headcount:
  // duration = N × 1.1s with per-agent 1.1s offsets.
  const rrCycle = Math.max(shown.length, 4) * 1.1;
  // Blast winner: stable per campaign (not always the first in line).
  const winIdx =
    shown.length > 1
      ? (campaign.id.charCodeAt(0) + campaign.id.charCodeAt(campaign.id.length - 1)) % shown.length
      : 0;
  return (
    <div className="lt-anim flex flex-wrap items-center gap-x-5 gap-y-4">
      <div className="flex flex-col items-center gap-1.5">
        <div className="lt-caller relative flex h-14 w-14 items-center justify-center rounded-full bg-background text-foreground ring-1 ring-border">
          <PhoneIncoming className="h-5 w-5" />
        </div>
        <span className="text-[11px] text-muted-foreground">Caller</span>
      </div>
      <div className="relative h-0.5 min-w-[80px] flex-1 rounded-full bg-border">
        {[0, 1, 2].map((i) => (
          <span key={i} className="lt-sig" style={{ animationDelay: `${i * 0.45}s` }} />
        ))}
        <span className="absolute -top-5 left-0 right-0 text-center text-[10px] font-medium text-muted-foreground">
          {rr ? "one agent at a time" : "everyone at once"}
        </span>
      </div>
      <div className="flex flex-wrap items-start gap-3">
        {shown.map((a, i) => {
          const p = presenceOf(a);
          return (
            <div key={a.id} className="flex w-14 flex-col items-center gap-1.5" title={`${a.name} · ${p.label}`}>
              <div
                className={cn(
                  "lt-avatar relative flex h-12 w-12 items-center justify-center rounded-full border-2 bg-background text-xs font-bold",
                  rr ? "lt-rr" : i === winIdx ? "lt-blast lt-win" : "lt-blast",
                )}
                style={
                  rr
                    ? ({ animationDelay: `${i * 1.1}s`, animationDuration: `${rrCycle}s`, "--lt-delay": `${i * 1.1}s`, "--lt-dur": `${rrCycle}s` } as React.CSSProperties)
                    : undefined
                }
              >
                {initials(a.name)}
                <span className={cn("absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background", p.dot)} />
              </div>
              <span className="max-w-full truncate text-[11px] leading-tight text-muted-foreground">
                {a.name.split(" ")[0]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverviewDashboard({ hasCampaigns }: { hasCampaigns: boolean }) {
  const { data } = useQuery({
    queryKey: ["live-transfer", "overview"],
    queryFn: () => fetchJson<Overview>(overviewUrl()),
    refetchInterval: 15_000,
  });

  if (!data || (!hasCampaigns && data.kpis.callsAllTime === 0)) return null;

  const { series, recent } = data;
  const maxDay = Math.max(...series.map((d) => d.calls), 1);
  const weekCalls = series.reduce((n, d) => n + d.calls, 0);
  const weekAnswered = series.reduce((n, d) => n + d.answered, 0);
  const weekMissed = weekCalls - weekAnswered;
  const today = series[series.length - 1]?.day;
  const dayLabel = (iso: string) =>
    new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "short" });
  const fullDay = (iso: string) =>
    new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  const isFresh = (iso: string) => Date.now() - new Date(iso).getTime() < 15 * 60_000;

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
      <section className="flex flex-col rounded-2xl border bg-card p-5">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-sm font-medium">Last 7 days</h3>
          {weekCalls > 0 && (
            <span className="text-xs tabular-nums text-muted-foreground">
              {Math.round((weekAnswered / weekCalls) * 100)}% answered
            </span>
          )}
        </div>
        <div className="mt-2 flex items-baseline gap-1.5">
          <span className="text-3xl font-semibold tabular-nums">{weekCalls}</span>
          <span className="text-sm text-muted-foreground">{weekCalls === 1 ? "call" : "calls"}</span>
        </div>
        <p className="text-xs tabular-nums">
          <span className="text-emerald-600">{weekAnswered} answered</span>
          <span className="text-muted-foreground"> · </span>
          <span className={weekMissed > 0 ? "text-red-600" : "text-muted-foreground"}>{weekMissed} missed</span>
        </p>

        {/* Columns have to stretch (no items-end here) or the bar percentages resolve to nothing and every day draws flat. */}
        <div
          className="mt-5 flex h-40 gap-2 lg:h-auto lg:min-h-40 lg:flex-1"
          role="img"
          aria-label={`Calls per day, last 7 days: ${weekCalls} total, ${weekAnswered} answered`}
        >
          {series.map((d) => {
            const missed = d.calls - d.answered;
            return (
              <div
                key={d.day}
                className="flex flex-1 flex-col items-center gap-1.5"
                title={`${fullDay(d.day)}: ${d.calls} ${d.calls === 1 ? "call" : "calls"}, ${d.answered} answered`}
              >
                <div className="flex w-full flex-1 items-end pt-4">
                  {d.calls > 0 ? (
                    <div className="relative w-full" style={{ height: `${(d.calls / maxDay) * 100}%`, minHeight: 6 }}>
                      <span className="absolute inset-x-0 -top-4 text-center text-[10px] font-medium tabular-nums">
                        {d.calls}
                      </span>
                      <div className="flex h-full w-full flex-col overflow-hidden rounded-md">
                        <div className="bg-red-400/70" style={{ flexGrow: missed }} />
                        <div className="bg-emerald-500" style={{ flexGrow: d.answered }} />
                      </div>
                    </div>
                  ) : (
                    <div className="h-1 w-full rounded-full bg-muted" />
                  )}
                </div>
                <span
                  className={cn(
                    "text-[10px] tabular-nums",
                    d.day === today ? "font-semibold text-foreground" : "text-muted-foreground",
                  )}
                >
                  {dayLabel(d.day)}
                </span>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex items-center gap-4 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-emerald-500" />
            Answered
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-red-400/70" />
            Missed
          </span>
        </div>
      </section>

      <section className="rounded-2xl border bg-card p-5">
        <h3 className="text-sm font-medium">Latest calls</h3>
        {recent.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            No calls yet. The first call to a live line shows up here within 15 seconds.
          </p>
        ) : (
          <ol className="mt-2 divide-y">
            {recent.slice(0, 6).map((e) => {
              const style = OUTCOME_STYLE[outcomeOf(e)];
              const detail = [outcomeDetail(e), e.leadAssignedToName ? `lead with ${e.leadAssignedToName}` : null]
                .filter(Boolean)
                .join(" · ");
              return (
                <li
                  key={e.id}
                  className={cn("-mx-2 flex items-center gap-3 rounded-md px-2 py-2 text-sm", isFresh(e.createdAt) && "lt-new")}
                >
                  <span className={cn("h-2 w-2 shrink-0 rounded-full", style.dot)} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">
                      {e.leadId ? (
                        <a href={`/leads/${e.leadId}`} className="font-medium hover:underline" title="Open lead to assign or add notes">
                          {callerLabel(e.leadName, e.callerNumber, e.unknownAiTransfer)}
                        </a>
                      ) : (
                        <span className="font-medium">{callerLabel(e.leadName, e.callerNumber, e.unknownAiTransfer)}</span>
                      )}
                      <span className="text-muted-foreground"> · {e.campaignName}</span>
                    </span>
                    {detail && <span className="block truncate text-xs text-muted-foreground">{detail}</span>}
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{timeAgo(e.createdAt)}</span>
                  <span className={cn("w-24 shrink-0 truncate text-right text-xs font-medium", style.text)}>
                    {outcomeLabel(e)}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}

/* ─── Campaign activity: who picked up, who didn't, where each lead went ── */

const fmtDuration = (secs: number | null) => {
  if (!secs || secs <= 0) return null;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const timeAgo = (iso: string) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/** Unknown callers get an auto-created "Live Transfer +1…" lead; the number reads better, unless it's the AI's own line. */
const callerLabel = (leadName: string | null, callerNumber: string, unknownAiTransfer?: boolean) =>
  unknownAiTransfer ? "Mortgage AI transfer"
    : leadName && !/^Live Transfer\b/i.test(leadName) ? leadName : fmtPhone(callerNumber);

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

const joinNames = (names: string[], max = 3) =>
  names.length <= max ? names.join(", ") : `${names.slice(0, max).join(", ")} +${names.length - max}`;

// Older backends send no outcome; fall back to the old rule.
const outcomeOf = (e: CallOutcome) => e.outcome ?? (e.answeredByName ? "answered" : "missed");

const OUTCOME_STYLE = {
  answered: { dot: "bg-emerald-500", text: "text-emerald-600", chip: "bg-emerald-500/10 text-emerald-600" },
  in_progress: { dot: "bg-amber-500", text: "text-amber-600", chip: "bg-amber-500/10 text-amber-600" },
  missed: { dot: "bg-red-500", text: "text-red-600", chip: "bg-red-500/10 text-red-500" },
} as const;

const outcomeLabel = (e: CallOutcome) => {
  const o = outcomeOf(e);
  if (o === "answered") return e.answeredByName || "Answered";
  return o === "in_progress" ? "Ringing" : "Missed";
};

/** What happened on one transfer: who rang, how long a missed caller waited, late pickups, voicemail. */
const outcomeDetail = (e: CallOutcome, withRang = true) =>
  [
    withRang && e.rangAgents?.length ? `Rang ${joinNames(e.rangAgents.map((a) => firstName(a.name)))}` : null,
    outcomeOf(e) === "missed" && e.waitedSeconds ? `caller waited ${fmtDuration(e.waitedSeconds)}` : null,
    e.lateClaimByName ? `${firstName(e.lateClaimByName)} picked up after they hung up` : null,
    e.voicemailLeft ? "left a voicemail" : null,
  ]
    .filter(Boolean)
    .join(" · ");

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** Appointments a partner booked through the Appointment API. */
function ApiAppointmentsPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ["live-transfer", "appointment-api", "appointments"],
    queryFn: () => fetchJson<ApiAppointmentsData>(endpoint.liveTransfer.appointmentApiAppointments),
    refetchInterval: 60_000,
  });
  const kpis = data?.kpis;
  const rows = data?.rows ?? [];
  const statusPill = (r: ApiAppointment) => {
    const st = (r.status || "scheduled").toLowerCase();
    if (st === "cancelled" || st === "canceled")
      return { label: "Cancelled", cls: "bg-red-500/10 text-red-600" };
    if (st === "completed") return { label: "Completed", cls: "bg-emerald-500/10 text-emerald-600" };
    if (new Date(r.startTime).getTime() < Date.now())
      return { label: "Past", cls: "bg-muted text-muted-foreground" };
    return { label: "Upcoming", cls: "bg-sky-500/10 text-sky-600" };
  };

  return (
    <div className="rounded-xl border">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <CalendarClock className="h-4.5 w-4.5" />
          </div>
          <div>
            <div className="text-sm font-semibold">Appointments booked through the API</div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Callbacks your partners set instead of transferring live.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 text-center tabular-nums">
          {[
            ["Booked", kpis?.total],
            ["Upcoming", kpis?.upcoming],
            ["Last 7 days", kpis?.last7d],
            ["Cancelled", kpis?.cancelled],
          ].map(([label, n]) => (
            <div key={String(label)} className="rounded-lg bg-muted/40 px-3 py-1.5">
              <div className="text-base font-semibold">{n ?? "–"}</div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 border-t px-4 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading bookings…
        </div>
      ) : rows.length === 0 ? (
        <p className="border-t px-4 py-6 text-sm text-muted-foreground">
          No bookings yet. When a partner posts to the Appointment API, the appointment shows up here.
        </p>
      ) : (
        <div className="divide-y border-t">
          {rows.map((r) => {
            const pill = statusPill(r);
            return (
              <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
                <div className="min-w-0 flex-1 basis-40">
                  {r.leadId ? (
                    <a href={`/leads/${r.leadId}`} className="font-medium hover:underline">
                      {r.leadName}
                    </a>
                  ) : (
                    <span className="font-medium">{r.leadName}</span>
                  )}
                  <div className="truncate text-xs text-muted-foreground">
                    {[r.leadPhone ? fmtPhone(r.leadPhone) : null, r.leadState].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="basis-44 tabular-nums">
                  <div>{fmtWhen(r.startTime)}</div>
                  <div className="text-xs text-muted-foreground">booked {timeAgo(r.bookedAt)}</div>
                </div>
                <div className="min-w-0 basis-40 text-xs text-muted-foreground">
                  <div className="truncate text-sm text-foreground">{r.agentName || "Unassigned"}</div>
                  <div className="truncate">{r.campaignName || (r.campaignPhone ? fmtPhone(r.campaignPhone) : ", ")}</div>
                </div>
                <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", pill.cls)}>{pill.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Round Blast: one signal pushed out to everyone at once. */
const BlastGlyph = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" className={className} aria-hidden>
    <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
    <path d="M8.6 8.6a4.8 4.8 0 0 0 0 6.8M15.4 8.6a4.8 4.8 0 0 1 0 6.8" />
    <path d="M5.4 5.4a9.3 9.3 0 0 0 0 13.2M18.6 5.4a9.3 9.3 0 0 1 0 13.2" opacity="0.55" />
  </svg>
);

/** Round Robin: the ring moves around the list, one agent per call. */
const RobinGlyph = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="M20 12a8 8 0 1 1-2.6-5.9" />
    <path d="M20.5 3.5v4.6h-4.6" />
    <circle cx="12" cy="8.2" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="8.4" cy="14.4" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="15.6" cy="14.4" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

/** Dot colour + wording for an agent's presence, in ring terms (online = would get rung). */
const presenceOf = (p: Presence) => {
  if (p.online) {
    if (p.userStatus === "on_call") return { dot: "bg-sky-500", label: "On a call · will still ring", short: "On call" };
    if (p.userStatus === "idle") return { dot: "bg-amber-400", label: "Idle · will ring", short: "Idle" };
    return { dot: "bg-emerald-500", label: "Online · will ring", short: "Online" };
  }
  const seen = p.lastSeenAt ? timeAgo(p.lastSeenAt) : null;
  const recent = p.lastSeenAt && Date.now() - new Date(p.lastSeenAt).getTime() < 20 * 60_000;
  if (p.userStatus === "dnd" && recent) {
    return { dot: "bg-red-500", label: "Do not disturb · skipped", short: "DND" };
  }
  return {
    dot: "bg-slate-300 dark:bg-slate-600",
    label: seen ? `Offline · last online ${seen}` : "Never signed in",
    short: seen ? `seen ${seen}` : "never",
  };
};

function CampaignActivityPanel({ campaign }: { campaign: Campaign }) {
  const { data, isLoading } = useQuery({
    queryKey: ["live-transfer", "activity", campaign.id],
    queryFn: () => fetchJson<CampaignActivity>(endpoint.liveTransfer.campaignActivity(campaign.id)),
    refetchInterval: 15_000,
  });

  const agentById = useMemo(
    () => new Map(campaign.agents.map((a) => [a.id, a])),
    [campaign.agents],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center border-t py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const events = data?.events ?? [];
  const stats = data?.agentStats ?? [];

  return (
    <div className="space-y-4 border-t bg-muted/20 p-4">
      {/* Scoreboard: answers vs missed rings per agent */}
      {stats.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-medium">Who picks up</p>
          <div className="flex flex-wrap gap-2">
            {stats.map((a) => {
              const missed = a.rang - a.answered;
              return (
                <div
                  key={a.userId}
                  className="flex items-center gap-2.5 rounded-lg border bg-card px-3 py-1.5"
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                    {initials(a.name)}
                  </div>
                  <span className="text-sm font-medium">{a.name}</span>
                  <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
                    <PhoneIncoming className="h-3 w-3" />
                    {a.answered}
                  </span>
                  {missed > 0 && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-red-500">
                      <PhoneMissed className="h-3 w-3" />
                      {missed}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Call feed */}
      <div>
        <p className="mb-2 text-sm font-medium">
          Recent calls{data?.totals ? <span className="text-muted-foreground"> · {data.totals.totalCalls} total</span> : null}
        </p>
        {events.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No calls yet. They appear here the moment this number rings.
          </p>
        ) : (
          <div className="space-y-1.5">
            {events.map((e) => {
              const out = outcomeOf(e);
              const style = OUTCOME_STYLE[out];
              const extra = outcomeDetail(e, false);
              const rang = e.rangAgents?.length
                ? e.rangAgents
                : e.rangAgentIds.map((id) => ({ id, name: agentById.get(id)?.name || "Agent" }));
              return (
                <div
                  key={e.id}
                  className="flex flex-wrap items-center gap-3 rounded-lg border bg-card px-3 py-2 text-sm"
                >
                  <span
                    className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full", style.chip)}
                  >
                    {out === "missed" ? <PhoneMissed className="h-3.5 w-3.5" /> : <PhoneIncoming className="h-3.5 w-3.5" />}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium">
                        {callerLabel(e.leadName, e.callerNumber, e.unknownAiTransfer)}
                      </span>
                      {e.leadId && (
                        <a
                          href={`/leads/${e.leadId}`}
                          className="text-primary hover:text-primary/80"
                          title="Open lead"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      {e.leadStage && (
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                          {e.leadStage}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {timeAgo(e.createdAt)} · rang {rang.length} {rang.length === 1 ? "agent" : "agents"}
                      {fmtDuration(e.durationSeconds) ? ` · talked ${fmtDuration(e.durationSeconds)}` : ""}
                      {extra ? ` · ${extra}` : ""}
                    </p>
                  </div>

                  {/* Who was rung — tiny avatar strip, answerer highlighted */}
                  <div className="flex -space-x-1.5">
                    {rang.slice(0, 6).map((ag) => {
                      const isAnswerer = ag.id === e.answeredById;
                      return (
                        <div
                          key={ag.id}
                          title={`${ag.name}${isAnswerer ? ", answered" : ""}`}
                          className={cn(
                            "flex h-6 w-6 items-center justify-center rounded-full border-2 border-background text-[9px] font-bold text-white",
                            isAnswerer
                              ? "z-10 bg-emerald-600 ring-2 ring-emerald-400"
                              : "bg-muted-foreground/70",
                          )}
                        >
                          {initials(ag.name)}
                        </div>
                      );
                    })}
                  </div>

                  <div className="shrink-0 text-right">
                    <p className={cn("text-xs font-semibold", style.text)}>{outcomeLabel(e)}</p>
                    {e.leadAssignedToName && (
                      <p className="text-[10px] text-muted-foreground">
                        lead → {e.leadAssignedToName}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── 3-step wizard: number → mode → agents ──────────────────────────────── */

function CampaignWizard({
  open,
  onOpenChange,
  campaign,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Editing keeps the number (it's what callers dial) — name, ring mode, agents change. */
  campaign?: Campaign | null;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!campaign;
  const firstStep = isEdit ? 1 : 0;
  const [step, setStep] = useState(firstStep);
  const [name, setName] = useState("");
  const [numberId, setNumberId] = useState<string>("");
  const [mode, setMode] = useState<Mode | "">("");
  // Where an AI-booked appointment lands. Default matches the backend column:
  // owner_triage, so a booking always surfaces on a calendar someone watches.
  const [apptMode, setApptMode] = useState<"owner_triage" | "auto_assign">("owner_triage");
  const [leadLabel, setLeadLabel] = useState(DEFAULT_LEAD_LABEL);
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  // TV commercial lines (per-user `tvTransfers` flag): a greeting before the ring, toll-free numbers.
  const { enabled: tvTransfers } = useFeatureFlag("tvTransfers");
  const [greetingOn, setGreetingOn] = useState(false);
  const [greeting, setGreeting] = useState(TV_GREETING);

  useEffect(() => {
    if (!open || !campaign) return;
    setStep(1);
    setName(campaign.name);
    setNumberId(campaign.phoneNumberId);
    setMode(campaign.mode);
    setApptMode(campaign.appointmentAssignmentMode ?? "owner_triage");
    setLeadLabel(campaign.leadLabel || DEFAULT_LEAD_LABEL);
    setGreetingOn(!!campaign.greeting);
    setGreeting(campaign.greeting || TV_GREETING);
    setAgentIds(campaign.agents.map((a) => a.id));
  }, [open, campaign]);

  const { data: numbers } = useQuery({
    queryKey: ["live-transfer", "numbers"],
    queryFn: () => fetchJson<AvailableNumber[]>(endpoint.liveTransfer.availableNumbers),
    enabled: open,
  });
  const { data: agents } = useQuery({
    queryKey: ["live-transfer", "agents"],
    queryFn: () => fetchJson<EligibleAgent[]>(endpoint.liveTransfer.eligibleAgents),
    enabled: open,
  });

  const selectedNumber = useMemo(
    () => numbers?.find((n) => n.id === numberId),
    [numbers, numberId],
  );

  const [agentSearch, setAgentSearch] = useState("");

  /**
   * Step 1 shows YOUR numbers first, with the rest of the hierarchy behind an opt-in.
   *
   * The endpoint returns every number in the agency tree, and rendering them all meant an
   * owner opened the wizard and saw a wall of other people's numbers — Bruce Wayne
   * looking at Tony Stark's and Dick Grayson's before his own. Yours is the common case;
   * reaching into a sub-agency should be a deliberate act. Search still spans everything.
   */
  const { user } = useUserContext();
  const currentUserId = (user?.user as any)?.id as string | undefined;
  const [showAllNumbers, setShowAllNumbers] = useState(false);

  const myNumbers = useMemo(
    () => (numbers ?? []).filter((n) => currentUserId && n.ownerUserId === currentUserId),
    [numbers, currentUserId],
  );
  const otherNumbers = useMemo(
    () => (numbers ?? []).filter((n) => !currentUserId || n.ownerUserId !== currentUserId),
    [numbers, currentUserId],
  );
  // Individual campaigns need to reach the chosen agent's number wherever it lives, so
  // that mode always sees the full list.
  const [numberSearch, setNumberSearch] = useState("");

  const visibleNumbers = useMemo(() => {
    const q = numberSearch.trim().toLowerCase();
    // ⚠️ Searching spans the WHOLE hierarchy, even while sub-agency numbers are collapsed.
    // Typing a number and being told it doesn't exist — because it was hidden behind a
    // toggle — is worse than showing it with its owner attached.
    const pool = q || showAllNumbers
      ? [...myNumbers, ...otherNumbers]
      : myNumbers;
    if (!q) return pool;
    const digits = q.replace(/\D/g, "");
    return pool.filter((n) => {
      const byOwner = (n.ownerName ?? "").toLowerCase().includes(q);
      // Match on digits so "313 284", "(313) 284-9367" and "3132849367" all work.
      const byNumber = digits.length > 0 && n.phoneNumber.replace(/\D/g, "").includes(digits);
      return byOwner || byNumber;
    });
  }, [numberSearch, showAllNumbers, myNumbers, otherNumbers]);

  // Agents grouped by agency — owner's agency first, then sub-agencies by
  // depth, each group A→Z. The search filters inside groups; a group with no
  // hits disappears entirely.
  const agencyGroups = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    const filtered = (agents || []).filter(
      (a) => !q || a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q),
    );
    const byAgency = new Map<string, { name: string; depth: number; agents: EligibleAgent[] }>();
    for (const a of filtered) {
      const g = byAgency.get(a.agencyId) || { name: a.agencyName, depth: a.depth ?? 99, agents: [] };
      g.agents.push(a);
      byAgency.set(a.agencyId, g);
    }
    return [...byAgency.entries()]
      .map(([id, g]) => ({ id, ...g, agents: [...g.agents].sort((x, y) => x.name.localeCompare(y.name)) }))
      .sort((x, y) => x.depth - y.depth || x.name.localeCompare(y.name));
  }, [agents, agentSearch]);

  /** Whole-agency toggle: adds every listed member, or removes them all. */
  const toggleAgency = (group: { agents: EligibleAgent[] }) => {
    const ids = group.agents.map((a) => a.id);
    const allIn = ids.every((id) => agentIds.includes(id));
    setAgentIds((prev) =>
      allIn ? prev.filter((id) => !ids.includes(id)) : [...prev, ...ids.filter((id) => !prev.includes(id))],
    );
  };

  const reset = () => {
    setStep(firstStep);
    setName("");
    setNumberId("");
    setMode("");
    setLeadLabel(DEFAULT_LEAD_LABEL);
    setGreetingOn(false);
    setGreeting(TV_GREETING);
    setAgentIds([]);
    setAgentSearch("");
    setError(null);
  };

  const buyTollFree = useMutation({
    mutationFn: () => fetchJsonPost<{ id: string; phoneNumber: string }>(endpoint.liveTransfer.tollFreeNumber),
    onSuccess: async (bought) => {
      await queryClient.invalidateQueries({ queryKey: ["live-transfer", "numbers"] });
      setNumberId(bought.id);
      setError(null);
    },
    onError: (e: any) => setError(e?.message || "Could not get a toll-free number. Try again."),
  });

  const create = useMutation({
    mutationFn: () =>
      handleApiCall(async () => {
        const body = {
          name:
            name.trim() ||
            `${mode === "round_blast" ? "Blast" : "Round Robin"} · ${fmtPhone(selectedNumber?.phoneNumber || campaign?.phoneNumber || "")}`,
          mode,
          appointmentAssignmentMode: apptMode,
          leadLabel: leadLabel.trim() || DEFAULT_LEAD_LABEL,
          agentIds,
          // Only flagged owners send it, so nobody else's save can touch a campaign's greeting.
          ...(tvTransfers ? { greeting: greetingOn ? greeting.trim() || null : null } : {}),
        };
        return campaign
          ? api.patch({ url: getApiUrl(endpoint.liveTransfer.campaign(campaign.id)), auth: true, body })
          : api.post({
              url: getApiUrl(endpoint.liveTransfer.campaigns),
              auth: true,
              body: { ...body, phoneNumberId: numberId },
            });
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["live-transfer"] });
      onOpenChange(false);
      reset();
    },
    onError: (e: any) =>
      setError(e?.message || (isEdit ? "Could not save the campaign. Try again." : "Could not create the campaign. Try again.")),
  });

  const toggleAgent = (id: string) =>
    setAgentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const canNext = step === 0 ? !!numberId : step === 1 ? !!mode : agentIds.length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit ${campaign.name}` : "New Live Transfer campaign"}
          </DialogTitle>
          <DialogDescription>
            {isEdit && `${fmtPhone(campaign.phoneNumber)} · `}
            {["Pick the number", "Pick how it rings", "Pick the agents"][step]} · step{" "}
            {step - firstStep + 1} of {3 - firstStep}
          </DialogDescription>
        </DialogHeader>

        {/* Step dots */}
        <div className="flex items-center gap-2">
          {(isEdit ? [1, 2] : [0, 1, 2]).map((i) => (
            <div
              key={i}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-colors",
                i <= step ? "bg-primary" : "bg-muted",
              )}
            />
          ))}
        </div>

        {/* ① Number */}
        {step === 0 && (
          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {/* Moe Ray's flow starts "choose a NEW number", but buying goes through Stripe
                checkout, so it can't be a step in this modal. This hands off to the
                purchase screen instead of the old dead end that just said "purchase a
                number first, then come back". */}
            <a
              href="/settings?tab=area-codes"
              className="flex w-full items-center justify-between rounded-lg border border-dashed p-3 text-left transition-colors hover:bg-muted/50"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Plus className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-semibold">Get a new number</p>
                  <p className="text-xs text-muted-foreground">
                    Buy one for this ring group, then come back and pick it here
                  </p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </a>

            {/* TV lines: bought on the spot, free, no checkout. One per ad shows which ad brings calls. */}
            {tvTransfers && (
              <button
                type="button"
                disabled={buyTollFree.isPending}
                onClick={() => buyTollFree.mutate()}
                className="flex w-full items-center gap-3 rounded-lg border border-dashed p-3 text-left transition-colors hover:bg-muted/50 disabled:cursor-wait disabled:opacity-60"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  {buyTollFree.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tv className="h-4 w-4" />}
                </span>
                <div>
                  <p className="font-semibold">{buyTollFree.isPending ? "Getting a toll-free number…" : "Get a toll-free number for a TV ad"}</p>
                  <p className="text-xs text-muted-foreground">
                    Free and picked for you right away. Give each ad its own number to see which one brings calls.
                  </p>
                </div>
              </button>
            )}

            {(numbers?.length ?? 0) > 3 && (
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search number or owner…"
                  value={numberSearch}
                  onChange={(e) => setNumberSearch(e.target.value)}
                  className="h-9 pl-8"
                />
              </div>
            )}

            {!visibleNumbers.length ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {numberSearch.trim()
                  ? `No number or owner matches “${numberSearch.trim()}”.`
                  : otherNumbers.length
                    ? "None of your own numbers are free, grab one above, or use a sub-agency number below."
                    : "No free numbers in your agency yet, grab one above."}
              </p>
            ) : (
              visibleNumbers.map((n) => (
                <button
                  key={n.id}
                  disabled={n.inUse}
                  onClick={() => setNumberId(n.id)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors",
                    n.inUse
                      ? "cursor-not-allowed opacity-50"
                      : numberId === n.id
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted/50",
                  )}
                >
                  <div>
                    <p className="font-semibold tabular-nums">{fmtPhone(n.phoneNumber)}</p>
                    <p className="text-xs text-muted-foreground">
                      {n.inUse
                        ? `In use by “${n.campaignName}”`
                        : n.ownerName
                          ? `Owner: ${n.ownerName}${
                              currentUserId && n.ownerUserId === currentUserId ? " · you" : ""
                            }`
                          : "Agency number"}
                    </p>
                  </div>
                  {numberId === n.id && <Check className="h-4 w-4 text-primary" />}
                </button>
              ))
            )}

            {/* Sub-agency numbers are opt-in — see your own first, reach down deliberately. */}
            {otherNumbers.length > 0 && !numberSearch.trim() && (
              <button
                type="button"
                onClick={() => setShowAllNumbers((v) => !v)}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50"
              >
                {showAllNumbers
                  ? "Show only my numbers"
                  : `Show sub-agency numbers (${otherNumbers.length})`}
                <ChevronDown
                  className={cn("h-3.5 w-3.5 transition-transform", showAllNumbers && "rotate-180")}
                />
              </button>
            )}
          </div>
        )}

        {/* ② Mode */}
        {step === 1 && (
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              onClick={() => setMode("round_robin")}
              className={cn(
                "rounded-xl border p-5 text-left transition-colors",
                mode === "round_robin" ? "border-primary bg-primary/5" : "hover:bg-muted/50",
              )}
            >
              <Radio className="h-6 w-6 text-primary" />
              <p className="mt-3 font-semibold">Round Robin</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Calls rotate through your list, each call rings the next agent in line.
              </p>
              <div className="mt-4">
                <RingVisual mode="round_robin" compact />
              </div>
            </button>
            <button
              onClick={() => setMode("round_blast")}
              className={cn(
                "rounded-xl border p-5 text-left transition-colors",
                mode === "round_blast" ? "border-amber-500 bg-amber-500/5" : "hover:bg-muted/50",
              )}
            >
              <Zap className="h-6 w-6 text-amber-500" />
              <p className="mt-3 font-semibold">Round Blast</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Every agent rings at once, first to pick up secures the lead.
              </p>
              <div className="mt-4">
                <RingVisual mode="round_blast" compact />
              </div>
            </button>
          </div>
        )}

        {/* ③ Agents */}
        {step === 2 && (
          <div className="space-y-3">
            {/* Appointment routing. Separate from the RING mode above: ringing
                is about who picks up now, this is about who owns a callback the
                AI booked because the caller did not want transferring. */}
            <div className="rounded-lg border p-3">
              <p className="text-sm font-semibold">When the AI books a callback</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Only applies to appointments made through the Appointment API.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setApptMode("owner_triage")}
                  className={cn(
                    "rounded-lg border p-3 text-left transition-colors",
                    apptMode === "owner_triage" ? "border-primary bg-primary/5" : "hover:bg-muted/50",
                  )}
                >
                  <p className="text-sm font-medium">Send to the agency owner&apos;s calendar</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Lands on the agency owner&apos;s calendar, unassigned, and the owner hands it to an agent.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setApptMode("auto_assign")}
                  className={cn(
                    "rounded-lg border p-3 text-left transition-colors",
                    apptMode === "auto_assign" ? "border-primary bg-primary/5" : "hover:bg-muted/50",
                  )}
                >
                  <p className="text-sm font-medium">Assign automatically</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Rotates through the agents below, skipping anyone already booked.
                  </p>
                </button>
              </div>
            </div>

            <Input
              placeholder="Campaign name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
            <div className="space-y-1">
              <label htmlFor="lt-lead-label" className="text-sm font-medium">Tag &amp; Lead Source</label>
              <Input
                id="lt-lead-label"
                placeholder={DEFAULT_LEAD_LABEL}
                value={leadLabel}
                onChange={(e) => setLeadLabel(e.target.value)}
                maxLength={60}
              />
              <p className="text-xs text-muted-foreground">
                Put on every lead this campaign rings for, so agents can filter to them. Leads from your own
                import lists keep their Lead Source and just get the tag.
              </p>
            </div>
            {tvTransfers && (
              <div className="space-y-1.5 rounded-lg border p-3">
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor="lt-greeting-on" className="text-sm font-medium">Greeting before the ring</label>
                  <Switch
                    id="lt-greeting-on"
                    checked={greetingOn}
                    onCheckedChange={(on) => {
                      setGreetingOn(on);
                      // A TV line's callers shouldn't be filed as mortgage transfers by accident.
                      if (on && leadLabel.trim() === DEFAULT_LEAD_LABEL) setLeadLabel(TV_LEAD_LABEL);
                    }}
                  />
                </div>
                {greetingOn && (
                  <Textarea
                    aria-label="Greeting text"
                    value={greeting}
                    onChange={(e) => setGreeting(e.target.value)}
                    maxLength={500}
                    rows={3}
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  {greetingOn
                    ? "Callers hear this, say their name, then your agents ring with that name on the lead."
                    : "Off: calls ring your agents straight away."}
                </p>
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search agents…"
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                  className="h-9 pl-8"
                />
              </div>
              <span
                className={cn(
                  "shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums transition-colors",
                  agentIds.length
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {agentIds.length} selected
              </span>
              {agentIds.length > 0 && (
                <button
                  onClick={() => setAgentIds([])}
                  className="shrink-0 text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="max-h-64 space-y-3 overflow-y-auto rounded-xl border bg-muted/20 p-2 pr-1.5">
              {!agencyGroups.length ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {agentSearch ? "No agents match your search." : "No active agents found in your agency."}
                </p>
              ) : (
                agencyGroups.map((g) => {
                  const pickedInGroup = g.agents.filter((a) => agentIds.includes(a.id)).length;
                  const allIn = pickedInGroup === g.agents.length;
                  return (
                    <div key={g.id} className="overflow-hidden rounded-lg border bg-card">
                      {/* Agency header — one tap adds/removes the whole team. */}
                      <button
                        onClick={() => toggleAgency(g)}
                        className="flex w-full items-center gap-2.5 border-b bg-muted/40 px-3 py-2 text-left transition-colors hover:bg-muted/70"
                      >
                        <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate text-xs font-bold uppercase tracking-wide">
                          {g.name}
                          {g.depth === 0 && (
                            <span className="ml-1.5 font-medium normal-case tracking-normal text-muted-foreground">
                              · your agency
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                          {pickedInGroup}/{g.agents.length}
                        </span>
                        <span
                          className={cn(
                            "shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold transition-colors",
                            allIn
                              ? "bg-primary text-primary-foreground"
                              : "bg-primary/10 text-primary",
                          )}
                        >
                          {allIn ? "Remove all" : "Add all"}
                        </span>
                      </button>
                      <div className="divide-y">
                        {g.agents.map((a) => {
                          const idx = agentIds.indexOf(a.id);
                          const on = idx >= 0;
                          return (
                            <button
                              key={a.id}
                              onClick={() => toggleAgent(a.id)}
                              className={cn(
                                "flex w-full items-center gap-3 px-3 py-2 text-left transition-colors",
                                on ? "bg-primary/[0.06]" : "hover:bg-muted/40",
                              )}
                            >
                              <div
                                className={cn(
                                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white transition-all",
                                  on
                                    ? "bg-primary ring-2 ring-primary/40"
                                    : "bg-muted-foreground/70",
                                )}
                              >
                                {initials(a.name)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={cn("flex items-center gap-1.5 truncate text-sm", on ? "font-semibold" : "font-medium")}>
                                  <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", presenceOf(a).dot)} />
                                  <span className="truncate">{a.name}</span>
                                </p>
                                <p className="truncate text-xs text-muted-foreground">
                                  {a.email} · {presenceOf(a).short}
                                </p>
                              </div>
                              {on &&
                                (mode === "round_robin" ? (
                                  <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">
                                    #{idx + 1}
                                  </span>
                                ) : (
                                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary">
                                    <Check className="h-3 w-3 text-primary-foreground" />
                                  </span>
                                ))}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {mode === "round_robin" && agentIds.length > 1 && (
              <p className="text-xs text-muted-foreground">
                Selection order is the rotation order, #1 gets the first call.
              </p>
            )}
            <div className="flex items-start gap-2 rounded-lg border-l-2 border-l-amber-400 bg-amber-50/60 p-3 dark:bg-amber-900/10">
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-300">
                {mode === "round_blast"
                  ? `Every call to ${fmtPhone(selectedNumber?.phoneNumber || campaign?.phoneNumber || "")} will ring all ${agentIds.length || "selected"} agents in their CRM at the same time. First to answer takes the call, everyone else's ring stops.`
                  : `Calls to ${fmtPhone(selectedNumber?.phoneNumber || campaign?.phoneNumber || "")} will rotate through your list, one agent per call.`}
              </p>
            </div>
          </div>
        )}

        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between pt-1">
          <Button
            variant="ghost"
            className="gap-1.5"
            disabled={step === firstStep}
            onClick={() => setStep((s) => Math.max(firstStep, s - 1))}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          {step < 2 ? (
            <Button className="gap-1.5" disabled={!canNext} onClick={() => setStep((s) => s + 1)}>
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              className="gap-1.5"
              disabled={!canNext || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {isEdit ? "Save changes" : "Create campaign"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
